/* ==========================================================================
   Plant Store <> UnfoldCRO — live Judge.me reviews in the custom reviews rail

   Replaces the hand-entered review blocks in .customer-reviews__scroller with
   real Judge.me reviews, rendered into the same markup so the design is
   untouched.

   Where the data comes from: cdn.judge.me/reviews/reviews_for_carousel — the
   same public endpoint Judge.me's own carousel widget calls. It needs no API
   token, answers with Access-Control-Allow-Origin: * and is CDN-cached for
   20 minutes, so this costs the shopper one cheap request. The Judge.me app
   block does not have to be on the page at all.

   If the request fails, returns nothing usable, or the shop has no reviews yet,
   the merchant's own blocks are simply left in place — the section never ends
   up empty because a third party was having a bad day.
   ========================================================================== */
(function () {
  'use strict';

  var ENDPOINT = 'https://cdn.judge.me/reviews/reviews_for_carousel';

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Two initials, matching what the Liquid does for the manual blocks.
  function initials(name) {
    var parts = String(name || '').trim().split(/\s+/);
    var out = '';
    for (var i = 0; i < parts.length && out.length < 2; i++) {
      if (parts[i]) out += parts[i].charAt(0);
    }
    return (out || 'R').toUpperCase().slice(0, 2);
  }

  function timeAgo(iso) {
    var then = Date.parse(iso);
    if (isNaN(then)) return '';

    var days = Math.floor((Date.now() - then) / 86400000);
    if (days < 1) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return days + ' days ago';

    var months = Math.round(days / 30);
    if (months < 12) return months === 1 ? 'a month ago' : months + ' months ago';

    var years = Math.round(days / 365);
    return years === 1 ? 'a year ago' : years + ' years ago';
  }

  function stars(rating) {
    var on = Math.round(Number(rating) || 0);
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += '<span class="review-card__star' + (i <= on ? '' : ' review-card__star--off') + '">★</span>';
    }
    return out;
  }

  // Shopify CDN images take a width param; Judge.me's own image host already
  // carries one, so it is left alone.
  function sized(url, width) {
    if (!url) return '';
    if (url.indexOf('cdn.shopify.com') === -1) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function card(review, opts) {
    var name = review.reviewer_name || 'Anonymous';
    var meta = [];
    if (opts.showLocation && review.location) meta.push(review.location);
    var when = timeAgo(review.created_at);
    if (when) meta.push(when);

    var image = review.product_variant_image_url || '';
    var title = review.product_title || '';
    var url = review.product_in_store && review.product_url ? review.product_url : '';

    // swiper-slide because the rail is a Swiper once the section initialises
    // it — see the section's script.
    var html = '<div class="review-card swiper-slide">';

    html += '<div class="review-card__header">';
    html += '<span class="review-card__avatar">' + esc(initials(name)) + '</span>';
    html += '<div class="review-card__author-info">';
    html += '<span class="review-card__author-name">' + esc(name) + '</span>';
    html += '<span class="review-card__author-meta">' + esc(meta.join(' · ')) + '</span>';
    html += '</div>';
    if (review.verified_buyer) html += '<span class="review-card__badge">Verified</span>';
    html += '</div>';

    html += '<div class="review-card__stars">' + stars(review.rating) + '</div>';

    if (review.title) html += '<span class="review-card__title">' + esc(review.title) + '</span>';
    // review.body is plain text; body_html is the same content wrapped in <p>.
    // Using the plain field means nothing from the API is ever injected as
    // markup — a review is shopper-supplied content.
    if (review.body) html += '<p class="review-card__body">' + esc(review.body) + '</p>';

    if (title) {
      html += '<a' + (url ? ' href="' + esc(url) + '"' : '') + ' class="review-card__product-link">';
      html += image
        ? '<img src="' + esc(sized(image, 100)) + '" alt="' + esc(title) +
          '" loading="lazy" width="38" height="38" class="review-card__product-img">'
        : '<div class="review-card__product-placeholder"></div>';
      html += '<span class="review-card__product-title">' + esc(title) + '</span>';
      html += '</a>';
    }

    html += '</div>';
    return html;
  }

  /* The whole summary card — score, total and all five bars — is already
     rendered from Judge.me's shop metafields by the time this runs. Liquid has
     no thousands-separator filter, so this is the one thing left to do: turn
     6331 into 6,331 wherever a four-figure number appears in the card. */
  function groupDigits(root) {
    var targets = root.querySelectorAll('[data-jm-total-text], .customer-reviews__bar-val');

    Array.prototype.forEach.call(targets, function (el) {
      el.textContent = el.textContent.replace(/\d{4,}/g, function (digits) {
        return Number(digits).toLocaleString();
      });
    });
  }

  /* The section builds a Swiper over these cards, and Swiper measures its
     slides at construction — so it has to wait until the rail holds its final
     contents. Fired on every path, success or not, so the slider is never left
     waiting on a request that failed. */
  function ready(root) {
    root.dispatchEvent(new CustomEvent('ufc:reviews:ready', { bubbles: true }));
  }

  function init(root) {
    if (root.dataset.jmBound === '1') return;
    root.dataset.jmBound = '1';

    groupDigits(root);

    var scroller = root.querySelector('.customer-reviews__scroller');
    if (!scroller) return ready(root);

    var shop = root.getAttribute('data-jm-shop');
    if (!shop) return ready(root);

    var want = parseInt(root.getAttribute('data-jm-count'), 10) || 20;
    var minRating = parseInt(root.getAttribute('data-jm-min-rating'), 10) || 1;

    var params = [
      'reviews_selection=all',
      'carousel_type=cards',
      'star_rating=all',
      // Asks for more than needed, because the minimum-rating filter below
      // runs on this side and would otherwise thin the rail out.
      'max_reviews=' + Math.min(50, want * 2),
      'url=' + encodeURIComponent(root.getAttribute('data-jm-url') || window.location.origin),
      'shop_domain=' + encodeURIComponent(shop),
      'platform=shopify',
      'primary_language=' + encodeURIComponent(document.documentElement.lang || 'en'),
      'display_order=' + encodeURIComponent(root.getAttribute('data-jm-order') || 'media_first')
    ];

    var opts = { showLocation: root.getAttribute('data-jm-location') === 'true' };

    fetch(ENDPOINT + '?' + params.join('&'), { credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) throw new Error('judge.me responded ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var reviews = (data && data.reviews) || [];

        reviews = reviews.filter(function (review) {
          return (Number(review.rating) || 0) >= minRating && (review.body || review.title);
        });

        // Nothing usable — leave the merchant's own blocks alone.
        if (!reviews.length) return ready(root);

        var html = reviews.slice(0, want).map(function (review) {
          return card(review, opts);
        });

        // Into .swiper-wrapper, not the scroller — Swiper needs that element
        // to exist and to be the direct parent of the slides.
        var track = scroller.querySelector('.swiper-wrapper') || scroller;
        track.innerHTML = html.join('');
        root.setAttribute('data-jm-loaded', 'true');
        // The autoplay in the section reads scrollWidth per tick, so swapping
        // the cards needs no handshake — but reset the position, since the old
        // cards may have left the rail scrolled somewhere meaningless.
        scroller.scrollLeft = 0;
        ready(root);
      })
      .catch(function (error) {
        root.setAttribute('data-jm-loaded', 'failed');
        if (window.console) console.warn('[ufc] Judge.me reviews unavailable:', error.message);
        ready(root);
      });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-jm-shop]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
